/**
 * The C64's view of the level from a player's sprite position (rebb64 D_E9B8): a pointer into the
 * 40-byte-wide collision map at $8500 (32 columns of tiles, bit 7 = solid) one column left of the
 * body's top-left tile, plus the body's fine X and Y offsets within its tile. The player code then
 * reads the map at fixed byte offsets from that pointer ($51 = two rows down, same column...), so
 * those offsets are kept verbatim here.
 */

import { solidTile, ROWS } from '../playfield';
import type { World } from '../types';

/** What D_E9B8 leaves behind: INPFLG's row and column, $23 and $24. */
export interface Probe {
  /** Map row of the body's top tile: (Y - $15) / 8 - 3 through table $AC03. */
  row: number;
  /** Map column the pointer is at: the body's left tile column minus one. */
  col: number;
  /** $23: (X - $14) & 7. Non-zero: the body spans three tile columns. */
  fx: number;
  /** $24 (INDEX2): (Y - $15) & 7. Non-zero: the body spans three tile rows. */
  fy: number;
}

/** D_E9B8 for a sprite at (x, y), 8-bit arithmetic as on the C64. */
export function probe(x: number, y: number): Probe {
  const px = (x - 0x14) & 0xff;
  const py = (y - 0x15) & 0xff;
  return { row: (py >> 3) - 3, col: (px >> 3) - 1, fx: px & 7, fy: py & 7 };
}

/**
 * Is the map byte at `(INPFLG),off` solid? `off` is the 6502 index: 40 per row, 1 per column.
 * D_3A6C copies the top row into the three rows above the map and the bottom row into the three
 * below, so reads past the edges (which the wrap makes happen) see the top and bottom walls, holes
 * and all.
 */
export function solidAt(world: World, p: Probe, off: number): boolean {
  const row = p.row + Math.floor(off / 40);
  const col = p.col + (off % 40);
  return solidTile(world.playfield, col, Math.max(0, Math.min(ROWS - 1, row)));
}

/**
 * The C64's two-or-three column test: the byte at `off`, the next, and (when the body is off the
 * column grid, $23 != 0) the one after (the `ldy #off / iny / lda $23 / beq / iny` pattern).
 */
export function solidRun(world: World, p: Probe, off: number): boolean {
  return solidAt(world, p, off) || solidAt(world, p, off + 1) || (p.fx !== 0 && solidAt(world, p, off + 2));
}
